import { useCallback } from "react";
import { useTranslation } from "react-i18next";
import type { TFunction } from "i18next";
import { listTermUses } from "../../../api/knowledgeData/knowledgeDataApi";
import { CURATION_STATES } from "../../../api/knowledgeData/knowledgeDataDecoders";
import type { TermUse } from "../../../api/knowledgeData/lensDecoders";
import { formatTimeAgo } from "../../../shared/formatting/relativeTimes";
import { useLoad } from "../../../shared/loading/useLoad";
import { WorkspaceLink } from "../../../shell/workspace";
import EmptyState from "../../../ui/feedback/EmptyState";
import LoadStatus from "../../../ui/feedback/LoadStatus";
import DocumentVersionName from "../../sources/document/DocumentVersionName";
import CurationStateBadge from "../extractions/CurationStateBadge";
import { proposalPath } from "../extractions/proposalPath";
import OpenReview from "../results/OpenReview";

/**
 * A term in the data (F84 §9), the Data model's lens: the instance-data results whose
 * individuals have the term as their class, or whose statements have it as their property,
 * counted by state. Each opens its result's one view beside the term.
 */
export default function TermDataUses({ projectId, iri }: { projectId: string; iri: string }) {
  const { t } = useTranslation();
  const load = useCallback((signal: AbortSignal) => listTermUses(projectId, iri, signal), [projectId, iri]);
  const { state, reload } = useLoad(load);
  if (state.status !== "loaded") return <LoadStatus state={state} loadingLabel={t("common.loading")} onRetry={reload} />;
  const uses = state.value;
  if (uses.length === 0) return <EmptyState>{t("data.lenses.term.none")}</EmptyState>;
  return (
    <div className="space-y-2">
      <p className="text-sm">
        {CURATION_STATES.flatMap((curation) => {
          const inState = uses.filter((use) => use.state === curation);
          return inState.length === 0 ? [] : [t("data.lenses.term.byState", { state: t(`data.states.${curation}`), uses: usesText(inState, t) })];
        }).join(" · ")}
      </p>
      <ul className="space-y-1">
        {uses.map((use) => <TermUseRow key={use.source} use={use} />)}
      </ul>
    </div>
  );
}

function TermUseRow({ use }: { use: TermUse }) {
  const { t, i18n } = useTranslation();
  return (
    <li className="flex flex-wrap items-center gap-2 text-sm">
      <CurationStateBadge state={use.state} />
      <WorkspaceLink route={proposalPath(use.source)} placement="beside" className="app-link">
        {usesText([use], t)}
      </WorkspaceLink>
      <DocumentVersionName reference={use.document} />
      <span className="app-text-muted text-xs">{formatTimeAgo(use.recordedAt, i18n.resolvedLanguage)}</span>
      <OpenReview source={use.source} state={use.state} review={use.review} />
    </li>
  );
}

/** "2 individuals", "3 statements", or both: a class has individuals, a property statements. */
function usesText(uses: readonly TermUse[], t: TFunction): string {
  const individuals = uses.reduce((sum, use) => sum + use.individuals, 0);
  const statements = uses.reduce((sum, use) => sum + use.termStatements, 0);
  return [
    individuals === 0 ? null : t("data.lenses.term.individuals", { count: individuals }),
    statements === 0 ? null : t("data.lenses.term.statements", { count: statements }),
  ].filter((part) => part !== null).join(", ");
}
